import PasswordChecklist from 'react-password-checklist';

interface EmailPasswordFieldsProps {
  email: string;
  password: string;
  passwordRepeat: string;
  isSignup: boolean;
  isProcessing: boolean;
  passwordValid: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onPasswordRepeatChange: (value: string) => void;
  onPasswordValidChange: (isValid: boolean) => void;
  onForgotPasswordClick?: () => void;
}

/**
 * Email and password form fields with validation.
 * Shows password repeat and validation checklist in signup mode.
 */
export const EmailPasswordFields = ({
  email,
  password,
  passwordRepeat,
  isSignup,
  isProcessing,
  passwordValid,
  onEmailChange,
  onPasswordChange,
  onPasswordRepeatChange,
  onPasswordValidChange,
  onForgotPasswordClick,
}: EmailPasswordFieldsProps) => {
  return (
    <>
      {/* Email Field */}
      <div>
        <label htmlFor="email" className="block text-sm  text-main">
          Email address
        </label>
        <div className="mt-2">
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            disabled={isProcessing}
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            className="block w-full rounded-2xl border-0 bg-subtle px-4 py-3 text-main shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-disabled focus:bg-surface focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6 transition-all"
          />
        </div>
      </div>

      {/* Password Field */}
      <div>
        <label htmlFor="password" className="block text-sm  text-main">
          Password
        </label>
        <div className="mt-2">
          <input
            id="password"
            name="password"
            type="password"
            autoComplete={isSignup ? 'new-password' : 'current-password'}
            required
            disabled={isProcessing}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            className={`block w-full rounded-2xl border-0 bg-subtle px-4 py-3 text-main shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-disabled focus:bg-surface focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6 transition-all ${
              !isSignup || password.length === 0 || passwordValid
                ? ''
                : 'ring-red-300 focus:ring-red-500 bg-error-subtle'
            }`}
          />
        </div>
        {!isSignup && (
          <div className="mt-2 text-sm">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onForgotPasswordClick?.();
              }}
              className="text-brand hover:text-brand transition-colors"
            >
              Forgot password?
            </button>
          </div>
        )}
      </div>

      {/* Password Repeat Field (Signup Only) */}
      {isSignup && (
        <div>
          <label htmlFor="password-repeat" className="block text-sm  text-main">
            Repeat Password
          </label>
          <div className="mt-2">
            <input
              id="password-repeat"
              name="password-repeat"
              type="password"
              autoComplete="new-password"
              required
              disabled={isProcessing}
              value={passwordRepeat}
              onChange={(e) => onPasswordRepeatChange(e.target.value)}
              className={`block w-full rounded-2xl border-0 bg-subtle px-4 py-3 text-main shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-disabled focus:bg-surface focus:ring-2 focus:ring-inset focus:ring-teal-600 sm:text-sm sm:leading-6 transition-all ${
                !isSignup || password.length === 0 || passwordValid
                  ? ''
                  : 'ring-red-300 focus:ring-red-500 bg-error-subtle'
              }`}
            />
          </div>
          {password.length > 0 && (
            <div className="mt-4">
              <PasswordChecklist
                rules={['minLength', 'specialChar', 'number', 'capital', 'lowercase', 'match']}
                minLength={10}
                value={password}
                valueAgain={passwordRepeat}
                onChange={onPasswordValidChange}
                style={{ fontSize: '0.875rem' }}
                className=" text-muted"
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};
